import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, Switch, Card, Row, Col, 
  Divider, Alert, Typography, Space, Button, Tooltip, 
  Collapse, Tag, message
} from 'antd';
import {
  UserOutlined, MailOutlined, LockOutlined, SettingOutlined,
  EyeOutlined, EyeInvisibleOutlined, InfoCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import usuariosAdminService from '../services/usuariosAdminService';

const { Option } = Select;
const { Text, Title } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

const UsuarioModal = ({ visible, usuario, mode, onCancel, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rolesConfig, setRolesConfig] = useState({});
  const [selectedRole, setSelectedRole] = useState('');
  const [customPermissions, setCustomPermissions] = useState({});

  useEffect(() => {
    if (visible) {
      cargarConfiguracionRoles();
      
      if (usuario && (mode === 'edit' || mode === 'view')) {
        // Cargar datos del usuario existente
        form.setFieldsValue({
          usuario: usuario.usuario,
          nombre_persona: usuario.nombre_persona,
          email: usuario.email,
          rol: usuario.rol,
          activo: usuario.activo
        });
        setSelectedRole(usuario.rol);
        setCustomPermissions(usuario.permisos_json || {});
      } else {
        // Limpiar formulario para nuevo usuario
        form.resetFields();
        form.setFieldsValue({
          activo: true
        });
        setSelectedRole('');
        setCustomPermissions({});
      }
    }
  }, [visible, usuario, mode, form]);

  const cargarConfiguracionRoles = async () => {
    try {
      const config = await usuariosAdminService.obtenerConfiguracionRoles();
      setRolesConfig(config.roles);
    } catch (error) {
      console.error('Error cargando configuración de roles:', error);
    }
  };

  const handleRoleChange = (rol) => {
    setSelectedRole(rol);
    
    if (rolesConfig[rol]) {
      // Aplicar permisos por defecto del rol
      const permisosDefecto = rolesConfig[rol].permisos_defecto;
      setCustomPermissions(permisosDefecto);
      
      // Actualizar el formulario
      form.setFieldsValue({ rol });
    }
  };

  const handlePermissionChange = (recurso, accion, value) => {
    setCustomPermissions(prev => ({
      ...prev,
      [recurso]: {
        ...prev[recurso],
        [accion]: value
      }
    }));
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const userData = {
        ...values,
        permisos_json: customPermissions
      };

      if (mode === 'create') {
        await usuariosAdminService.crearUsuario(userData);
      } else if (mode === 'edit') {
        await usuariosAdminService.actualizarUsuario(usuario.id, userData);
      }

      onSuccess();
    } catch (error) {
      console.error('Error guardando usuario:', error);
      message.error(
        error.response?.data?.error || 
        `Error al ${mode === 'create' ? 'crear' : 'actualizar'} usuario`
      );
    } finally {
      setLoading(false);
    }
  };

  const getRoleInfo = () => {
    if (!selectedRole || !rolesConfig[selectedRole]) return null;
    
    const role = rolesConfig[selectedRole];
    return (
      <Alert
        message={role.nombre}
        description={role.descripcion}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  };

  const getModalTitle = () => {
    switch (mode) {
      case 'create': return 'Crear Nuevo Usuario';
      case 'edit': return 'Editar Usuario';
      case 'view': return 'Ver Detalles del Usuario';
      default: return 'Usuario';
    }
  };

  const renderPermissionsGrid = () => {
    const recursos = [
      {
        key: 'conjuntos',
        label: 'Gestión de Conjuntos',
        acciones: [
          { key: 'crear', label: 'Crear' },
          { key: 'editar', label: 'Editar' },
          { key: 'eliminar', label: 'Eliminar' },
          { key: 'ver', label: 'Ver' }
        ]
      },
      {
        key: 'usuarios',
        label: 'Gestión de Usuarios',
        acciones: [
          { key: 'crear', label: 'Crear' },
          { key: 'editar', label: 'Editar' },
          { key: 'eliminar', label: 'Eliminar' },
          { key: 'ver', label: 'Ver' }
        ]
      },
      {
        key: 'metricas',
        label: 'Métricas y Reportes',
        acciones: [
          { key: 'ver', label: 'Ver Dashboard' },
          { key: 'exportar', label: 'Exportar' }
        ]
      },
      {
        key: 'auditoria',
        label: 'Auditoría',
        acciones: [
          { key: 'ver', label: 'Ver Logs' }
        ]
      }
    ];

    return (
      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
        {recursos.map(recurso => (
          <Card 
            key={recurso.key}
            size="small" 
            title={
              <Space>
                <SettingOutlined />
                {recurso.label}
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Row gutter={[16, 8]}>
              {recurso.acciones.map(accion => (
                <Col span={12} key={accion.key}>
                  <Space>
                    <Switch
                      size="small"
                      checked={customPermissions[recurso.key]?.[accion.key] || false}
                      onChange={(checked) => handlePermissionChange(recurso.key, accion.key, checked)}
                      disabled={mode === 'view' || selectedRole === 'super_admin'}
                    />
                    <Text type={customPermissions[recurso.key]?.[accion.key] ? 'success' : 'secondary'}>
                      {accion.label}
                    </Text>
                  </Space>
                </Col>
              ))}
            </Row>
          </Card>
        ))}
      </div>
    );
  };

  const renderUserInfo = () => {
    if (mode !== 'view' || !usuario) return null;

    return (
      <Card title="Información del Usuario" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text strong>ID:</Text> <Text>{usuario.id}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Creado:</Text> <Text>{new Date(usuario.fecha_creacion).toLocaleDateString()}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Último Acceso:</Text> 
            <Text>
              {usuario.ultimo_acceso 
                ? new Date(usuario.ultimo_acceso).toLocaleString()
                : 'Nunca'
              }
            </Text>
          </Col>
          <Col span={12}>
            <Text strong>Sesiones Activas:</Text> 
            <Tag color={usuario.sesiones_activas > 0 ? 'green' : 'default'}>
              {usuario.sesiones_activas || 0}
            </Tag>
          </Col>
          <Col span={24}>
            <Text strong>Estado:</Text> 
            <Space>
              <Tag color={usuario.activo ? 'success' : 'error'}>
                {usuario.activo ? 'Activo' : 'Inactivo'}
              </Tag>
              {usuario.requiere_cambio_password && (
                <Tag color="warning">Requiere cambio de contraseña</Tag>
              )}
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <Modal
      title={getModalTitle()}
      open={visible}
      onCancel={onCancel}
      width={800}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        ...(mode !== 'view' ? [
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            {mode === 'create' ? 'Crear Usuario' : 'Actualizar Usuario'}
          </Button>
        ] : [])
      ]}
    >
      {renderUserInfo()}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={mode === 'view'}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Nombre de Usuario"
              name="usuario"
              rules={[
                { required: true, message: 'El usuario es requerido' },
                { min: 3, message: 'Mínimo 3 caracteres' },
                { max: 50, message: 'Máximo 50 caracteres' },
                { pattern: /^[a-zA-Z0-9_]+$/, message: 'Solo letras, números y guión bajo' }
              ]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="usuario123"
                maxLength={50}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'El email es requerido' },
                { type: 'email', message: 'Email inválido' },
                { max: 100, message: 'Máximo 100 caracteres' }
              ]}
            >
              <Input 
                prefix={<MailOutlined />} 
                placeholder="usuario@horebapps.com"
                maxLength={100}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Nombre Completo"
          name="nombre_persona"
          rules={[
            { required: true, message: 'El nombre es requerido' },
            { min: 2, message: 'Mínimo 2 caracteres' },
            { max: 150, message: 'Máximo 150 caracteres' }
          ]}
        >
          <Input 
            placeholder="Juan Pérez García"
            maxLength={150}
          />
        </Form.Item>

        {mode === 'create' && (
          <Form.Item
            label={
              <Space>
                Contraseña
                <Tooltip title="Mínimo 8 caracteres. Debe incluir mayúsculas, minúsculas, números y símbolos.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
            name="password"
            rules={[
              { required: true, message: 'La contraseña es requerida' },
              { min: 8, message: 'Mínimo 8 caracteres' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                message: 'Debe contener al menos: 1 mayúscula, 1 minúscula, 1 número y 1 símbolo'
              }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />}
              placeholder="Contraseña segura"
              iconRender={(visible) => 
                visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Rol"
              name="rol"
              rules={[{ required: true, message: 'Selecciona un rol' }]}
            >
              <Select 
                placeholder="Seleccionar rol"
                onChange={handleRoleChange}
                disabled={currentUser?.id === usuario?.id} // No permitir cambiar propio rol
              >
                {Object.entries(rolesConfig).map(([key, role]) => (
                  <Option key={key} value={key}>
                    <Space>
                      {role.nombre}
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        ({role.descripcion})
                      </Text>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Estado"
              name="activo"
              valuePropName="checked"
            >
              <Switch
                checkedChildren={<CheckCircleOutlined />}
                unCheckedChildren={<CloseCircleOutlined />}
                disabled={currentUser?.id === usuario?.id} // No permitir desactivar propio usuario
              />
            </Form.Item>
          </Col>
        </Row>

        {getRoleInfo()}

        {selectedRole && selectedRole !== 'super_admin' && (
          <Collapse>
            <Panel 
              header={
                <Space>
                  <SettingOutlined />
                  Permisos Específicos
                  <Text type="secondary">(Personalizar permisos del rol)</Text>
                </Space>
              } 
              key="permissions"
            >
              <Alert
                message="Configuración de Permisos"
                description="Personaliza los permisos específicos para este usuario. Los Super Admins tienen todos los permisos automáticamente."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              {renderPermissionsGrid()}
            </Panel>
          </Collapse>
        )}

        {selectedRole === 'super_admin' && (
          <Alert
            message="Super Administrador"
            description="Los Super Administradores tienen acceso completo a todas las funcionalidades del sistema."
            type="success"
            showIcon
          />
        )}
      </Form>
    </Modal>
  );
};

export default UsuarioModal;